import { useState } from "react"
import PropTypes from "prop-types"

import CollapsedConfigItem from "./collapsed_config_item"
import CustomFieldTextField from "./custom_field_text_field"
import TimeOfDayField from "shared/components/stored_fields/time_of_day_field"
import CustomFieldDropdownStaticField from "./custom_field_dropdown_static_field"
import { validatePopulator } from "partners/utils/field_validations"
import CustomFieldCheckboxMultiField from "./custom_field_checkbox_multi_field"
import InlineErrors from "shared/components/inline_errors"
import { customFieldShape } from "partners/components/shared/shared_prop_types"

const FIELD_UI_TYPES = [
  { value: "text_field", label: "Text field" },
  { value: "time_select", label: "Time" },
  { value: "collection_select", label: "Dropdown field" },
  { value: "checkbox_multi", label: "Checkboxes" },
]

const CustomField = ({
  index,
  customField,
  onCustomFieldChange,
  onCustomFieldRemove,
  updateAddButtonDisableStatus,
  validateNameUniqueness,
  dupeKeyNameWarning,
  disabled,
  errors,
  configUI,
  refToScroll,
}) => {
  const [customFieldErrors, updateErrors] = useState(errors)
  const handleCustomFieldChange = newFragment => {
    // Making a shallow clone.
    // Children components are expected to handle their own complex object cloning.
    // So newFragment is expected to be a clone already.
    onCustomFieldChange({ ...customField, ...newFragment })
  }

  const saveAndClose = () => {
    if (!validDropdownAndCheckbox()) return false

    handleCustomFieldChange({ collapsed: true })

    if (updateAddButtonDisableStatus) {
      updateAddButtonDisableStatus()
    }

    if (refToScroll.current) {
      refToScroll.current.scrollIntoView({ block: "center" })
    }
  }

  const validDropdownAndCheckbox = () => {
    if (
      !["collection_select", "checkbox_multi"].includes(customField.fieldUiType)
    )
      return true

    const populatorErrors = validatePopulator(
      customField.options || [],
      [],
      customField.fieldUiType
    )

    let hasDefaultErrors = []
    if (customField.required) {
      let invalidDefaultValue = false

      if (customField.fieldUiType == "collection_select") {
        invalidDefaultValue =
          !customField.options ||
          !customField.options
            .map(option => option.value)
            .includes(customField.defaultValue)
      } else if (customField.fieldUiType == "checkbox_multi") {
        invalidDefaultValue =
          !customField.defaultValue || customField.defaultValue.length == 0
      }

      invalidDefaultValue && hasDefaultErrors.push("Select a default option")
    }

    updateErrors({
      ...customFieldErrors,
      populator: populatorErrors,
      hasDefaultValue: hasDefaultErrors,
    })

    return populatorErrors.length == 0 && hasDefaultErrors.length == 0
  }

  const renderFieldTypeUI = () => {
    const handleDefaultValueChange = defaultValue =>
      handleCustomFieldChange({ defaultValue })

    switch (customField.fieldUiType) {
      case "text_field":
        return (
          <CustomFieldTextField
            index={index}
            defaultValue={customField.defaultValue}
            onDefaultValueChange={handleDefaultValueChange}
            disabled={disabled}
          />
        )
      case "time_select":
        return (
          <div className="field">
            <div className="label">
              <label>Default value</label>
            </div>

            <div className="input">
              <TimeOfDayField
                fieldName={customField.name}
                value={customField.defaultValue || "00:00"}
                onChange={handleDefaultValueChange}
                disabled={disabled}
              />
            </div>
          </div>
        )
      case "collection_select":
        return (
          <CustomFieldDropdownStaticField
            index={index}
            populator={customField.options}
            onPopulatorChange={options => handleCustomFieldChange({ options })}
            defaultValue={customField.defaultValue}
            hasDefaultValue={customField.required}
            onDefaultValueChange={handleDefaultValueChange}
            disabled={disabled}
            fieldType={customField.fieldUiType}
            errors={customFieldErrors}
          />
        )
      case "checkbox_multi":
        return (
          <CustomFieldCheckboxMultiField
            populator={customField.options}
            onPopulatorChange={options => handleCustomFieldChange({ options })}
            defaultValue={customField.defaultValue || []}
            hasDefaultValue={customField.required}
            onDefaultValueChange={handleDefaultValueChange}
            disabled={disabled}
            fieldType={customField.fieldUiType}
            errors={customFieldErrors}
          />
        )
      default:
        return null
    }
  }

  return configUI && customField.collapsed ? (
    <CollapsedConfigItem
      type="option"
      status={Object.keys(errors.all).length ? "dirty" : "valid"}
      title={customField.label}
      index={index + 1}
      onUpdateViewClick={() => {
        if (updateAddButtonDisableStatus) {
          updateAddButtonDisableStatus(true)
        }
        handleCustomFieldChange({ collapsed: false })
      }}
      onRemoveClick={onCustomFieldRemove}
    />
  ) : (
    <div className="list-item-form">
      {/*Label*/}
      <div className="field">
        <div className="label">
          <label htmlFor={`custom-field-label-${index}`}>Label</label>
        </div>
        <InlineErrors errors={errors.getAll("/label")} />
        <div className="input">
          <input
            type="text"
            name="label"
            id={`custom-field-label-${index}`}
            value={customField.label || ""}
            onChange={e => handleCustomFieldChange({ label: e.target.value })}
            required
          />
        </div>

        <p className="note">
          Include a leading statement — use a question where possible
        </p>
      </div>

      {/*Helper text*/}
      <div className="field">
        <div className="label">
          <label htmlFor={`custom-field-helperText-${index}`}>
            Optional helper text
          </label>
        </div>
        <div className="input">
          <input
            type="text"
            name="helperText"
            id={`custom-field-helperText-${index}`}
            value={customField.helperText || ""}
            onChange={e =>
              handleCustomFieldChange({ helperText: e.target.value })
            }
          />
        </div>
        <p className="note">Markdown-style links allowed</p>
      </div>

      {/*Key Name*/}
      <div className="field">
        <div className="label">
          <label htmlFor={`custom-field-name-${index}`}>Key name</label>
        </div>
        <InlineErrors
          errors={
            dupeKeyNameWarning.length > 1
              ? [...errors.getAll("/name"), dupeKeyNameWarning]
              : errors.getAll("/name")
          }
        />
        <div className="input">
          <input
            type="text"
            name="name"
            id={`custom-field-name-${index}`}
            value={customField.name || ""}
            minLength={4}
            pattern="^[a-z0-9-_]+$"
            disabled={disabled}
            onChange={e => handleCustomFieldChange({ name: e.target.value })}
            onBlur={e => validateNameUniqueness(e.target.value)}
            required
          />
        </div>
        <p className="note">Use snake_case</p>
      </div>

      {/*Required*/}
      <div className="field">
        <div className="label">
          <label>Required</label>
        </div>
        <div className="input">
          <div className="input-group">
            <input
              type="radio"
              id={`custom-field-required-true-${index}`}
              name={`required-${index}`}
              value="true"
              disabled={disabled}
              checked={customField.required}
              onChange={e =>
                handleCustomFieldChange({
                  required: e.target.value === "true",
                })
              }
            />
            <label
              htmlFor={`custom-field-required-true-${index}`}
              onClick={() =>
                handleCustomFieldChange({
                  required: true,
                })
              }
            >
              This field is required
            </label>
          </div>
          <div className="input-group">
            <input
              type="radio"
              id={`custom-field-required-false-${index}`}
              name={`required-${index}`}
              value="false"
              disabled={disabled}
              checked={!customField.required}
              onChange={e =>
                handleCustomFieldChange({
                  required: e.target.value === "true",
                })
              }
            />
            <label
              htmlFor={`custom-field-required-false-${index}`}
              onClick={() =>
                handleCustomFieldChange({
                  required: false,
                })
              }
            >
              This field is optional
            </label>
          </div>
        </div>
      </div>

      {/*Field type selector*/}
      <div className="field">
        <div className="label">
          <label htmlFor={`custom-field-fieldTypeSelector-${index}`}>
            Field type
          </label>
        </div>
        <InlineErrors errors={errors.getAll("/field_type")} />
        <div className="input">
          <select
            id={`custom-field-fieldTypeSelector-${index}`}
            name="fieldTypeSelector"
            value={customField.fieldUiType}
            disabled={disabled}
            onChange={e =>
              // Clear the default value and other extra fields when changing types
              handleCustomFieldChange({
                fieldUiType: e.target.value,
                defaultValue: null,
                defaultValueJson: null,
                options: null,
              })
            }
          >
            {FIELD_UI_TYPES.map(t => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/*Field type controls*/}
      {renderFieldTypeUI()}

      <button
        className="button-tertiary"
        disabled={disabled}
        onClick={e => {
          e.preventDefault()
          configUI ? saveAndClose() : onCustomFieldRemove()
        }}
      >
        {configUI ? "Done" : "Remove field"}
      </button>
    </div>
  )
}

CustomField.propTypes = {
  configUI: PropTypes.bool,
  customField: customFieldShape.isRequired,
  index: PropTypes.number.isRequired,
  onCustomFieldChange: PropTypes.func.isRequired,
  onCustomFieldRemove: PropTypes.func.isRequired,
  updateAddButtonDisableStatus: PropTypes.func,
  validateNameUniqueness: PropTypes.func.isRequired,
  dupeKeyNameWarning: PropTypes.string,
  disabled: PropTypes.bool,
  errors: PropTypes.object,
  refToScroll: PropTypes.object,
}

export default CustomField
